import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { T } from 'shared/helper/translate';
import { searchAbstracts, viewAbstract } from 'researcher/abstract/actions';
import Link from 'shared/components/Link';
import RubriqLoadingSpinner from '../../components/RubriqLoadingSpinner';
import './abstract-output.scss';
import RubriqAbstractOutputHeader from './RubriqAbstractOutputHeader';
import RubriqAbstractSection from './RubriqAbstractSection';
import { parseStructuredAbstract, buildKeywordsPlainText, buildKeywordsDocxParagraphs, buildSimpleSummaryDocxParagraphs, buildGraphicalAbstractDocxParagraphs, hasSimpleSummary, hasGraphicalAbstract, sanitizeFilename } from './abstractOutputHelpers';
var renderAbstractBody = function renderAbstractBody(data) {
  if (data.abstractType === 'structured') {
    var segments = parseStructuredAbstract(data.generatedAbstract);
    var hasHeadings = segments.some(function (segment) {
      return segment.heading;
    });
    if (hasHeadings) {
      return /*#__PURE__*/React.createElement("div", {
        className: "d-flex flex-column gap-2"
      }, segments.map(function (segment, index) {
        return (
          /*#__PURE__*/
          // eslint-disable-next-line react/no-array-index-key
          React.createElement("p", {
            key: index,
            className: "mb-0"
          }, segment.heading && /*#__PURE__*/React.createElement("strong", null, segment.heading, ": "), segment.body)
        );
      }));
    }
  }
  return /*#__PURE__*/React.createElement("p", {
    className: "mb-0",
    style: {
      whiteSpace: 'pre-line'
    }
  }, data.generatedAbstract);
};
var RubriqAbstractOutput = function RubriqAbstractOutput() {
  var _listItem$file;
  var _useParams = useParams(),
    documentIdentity = _useParams.documentIdentity;
  var dispatch = useDispatch();
  var _useSelector = useSelector(function (state) {
      return state;
    }),
    abstract = _useSelector.abstract;
  var viewed = abstract.viewedAbstracts[documentIdentity];
  var listItem = abstract.abstracts.find(function (a) {
    return a.identity === documentIdentity;
  });
  useEffect(function () {
    dispatch(viewAbstract(documentIdentity));
  }, [documentIdentity]);
  useEffect(function () {
    // Covers a direct/refreshed navigation to this URL, where the list (and its file
    // name) was never loaded because RubriqAbstract never mounted.
    if (!listItem && !abstract.status.searchCompleted) {
      dispatch(searchAbstracts());
    }
  }, [documentIdentity]);
  if (!viewed || viewed.loading) {
    return /*#__PURE__*/React.createElement(RubriqLoadingSpinner, {
      text: "loading abstract"
    });
  }
  if (viewed.error || !viewed.data) {
    return /*#__PURE__*/React.createElement("div", {
      className: "p-4 text-center text-danger"
    }, /*#__PURE__*/React.createElement(T, {
      source: "We were unable to load this abstract. Please try again."
    }));
  }
  var data = viewed.data;
  var fileName = (listItem === null || listItem === void 0 || (_listItem$file = listItem.file) === null || _listItem$file === void 0 ? void 0 : _listItem$file.name) || '';
  var filenameBase = sanitizeFilename(fileName || documentIdentity);
  var keywords = data.generatedKeywords || [];
  return /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-column gap-3"
  }, /*#__PURE__*/React.createElement(Link, {
    to: "/rubriq/abstract",
    className: "d-inline-flex align-items-center gap-1 fw-semibold"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: "fa-regular fa-arrow-left"
  }), /*#__PURE__*/React.createElement(T, {
    source: "Back to List"
  })), /*#__PURE__*/React.createElement("div", {
    className: "rubriq-abstract-output-card border"
  }, /*#__PURE__*/React.createElement(RubriqAbstractOutputHeader, {
    fileName: fileName,
    data: data
  }), /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-column gap-2 p-3 border-top"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "fs-5 fw-semibold m-0"
  }, /*#__PURE__*/React.createElement(T, {
    source: "Abstract"
  })), renderAbstractBody(data)), /*#__PURE__*/React.createElement(RubriqAbstractSection, {
    heading: "Keywords",
    copyTooltip: "Copy keywords",
    downloadTooltip: "Download keywords",
    filename: "".concat(filenameBase, "-keywords"),
    failureMessage: /*#__PURE__*/React.createElement(T, {
      source: "We were unable to generate Keywords. Please try again."
    }),
    getPlainText: function getPlainText() {
      return buildKeywordsPlainText(data);
    },
    getDocxParagraphs: function getDocxParagraphs() {
      return buildKeywordsDocxParagraphs(data);
    },
    hasContent: keywords.length > 0
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-wrap gap-2"
  }, keywords.map(function (keyword) {
    return /*#__PURE__*/React.createElement("span", {
      key: keyword,
      className: "badge rounded-pill text-bg-light border px-3 py-2 fw-normal"
    }, keyword);
  }))), hasSimpleSummary(data) && /*#__PURE__*/React.createElement(RubriqAbstractSection, {
    heading: "Simple Language Summary",
    icon: "fa-solid fa-check",
    accent: "blue",
    copyTooltip: "Copy simple language summary",
    downloadTooltip: "Download simple language summary",
    filename: "".concat(filenameBase, "-simple-language-summary"),
    failureMessage: /*#__PURE__*/React.createElement(T, {
      source: "We were unable to generate a Simple Language Summary. Please try again."
    }),
    getPlainText: function getPlainText() {
      return data.generatedSimpleSummary;
    },
    getDocxParagraphs: function getDocxParagraphs() {
      return buildSimpleSummaryDocxParagraphs(data);
    },
    hasContent: !!data.generatedSimpleSummary
  }, /*#__PURE__*/React.createElement("p", {
    className: "mb-0"
  }, data.generatedSimpleSummary)), hasGraphicalAbstract(data) && /*#__PURE__*/React.createElement(RubriqAbstractSection, {
    heading: "Graphical Abstract",
    icon: "fa-solid fa-shapes",
    accent: "purple",
    copyTooltip: "Copy graphical abstract",
    downloadTooltip: "Download graphical abstract",
    filename: "".concat(filenameBase, "-graphical-abstract"),
    failureMessage: /*#__PURE__*/React.createElement(T, {
      source: "We were unable to generate a Graphical Abstract. Please try again."
    }),
    getPlainText: function getPlainText() {
      return data.generatedGraphicalAbstractPrompt;
    },
    getDocxParagraphs: function getDocxParagraphs() {
      return buildGraphicalAbstractDocxParagraphs(data);
    },
    hasContent: !!data.generatedGraphicalAbstractPrompt
  }, /*#__PURE__*/React.createElement("p", {
    className: "mb-0"
  }, data.generatedGraphicalAbstractPrompt))));
};
export default RubriqAbstractOutput;